/**
 * Privacy-safe analytics events via Sentry metrics.
 *
 * Rules:
 * - Never log message content, financial data, or PII
 * - Only log action names, page names, and counts
 *
 * SCOPE: this file is Sentry only, and is deliberately limited to the auth
 * counters, API metrics, and Sentry user context that are actually wired up.
 *
 * Product events go to PostHog via src/analytics/posthog.ts, with the event
 * taxonomy in src/analytics/events.ts and docs/features/ANALYTICS_EVENTS.md.
 * Do not add product events here — one event should have one home, and the
 * previous version of this file had 13 exported helpers with zero call sites.
 */
import * as Sentry from "@sentry/react";
import { normalizeApiPath } from "./pathNormalization";

// ── Auth Events ──

export function trackLogin(method: "email" | "phone" | "google" | "apple") {
    Sentry.metrics.count("login", 1, {
        attributes: { method },
    });
}

export function trackLogout() {
    Sentry.metrics.count("logout", 1);
}

export function trackSignup() {
    Sentry.metrics.count("signup", 1);
}

export function trackOtpVerified() {
    Sentry.metrics.count("otp_verified", 1);
}

// ── API Metrics ──
//
// `path` is normalised here (ids to :id, capability tokens to :token, no
// query string): a raw path gives one series per record, and the public
// pages' tokens are the access to them.

export function trackApiRequest(method: string, path: string, status: number) {
    Sentry.metrics.count("api.request", 1, {
        attributes: { method, path: normalizeApiPath(path), status: String(status) },
    });
}

export function trackApiResponseTime(method: string, path: string, durationMs: number) {
    Sentry.metrics.distribution("api.response_time", durationMs, {
        attributes: { method, path: normalizeApiPath(path) },
        unit: "millisecond",
    });
}

export function trackApiError(method: string, path: string, status: number) {
    Sentry.metrics.count("api.request_error", 1, {
        attributes: { method, path: normalizeApiPath(path), status: String(status) },
    });
}

// ── User Context ──

export function identifyUser(userUuid: string, email?: string) {
    Sentry.setUser({
        id: userUuid,
        email: email || undefined,
        ip_address: "{{auto}}",
    });
}

export function clearUser() {
    Sentry.setUser(null);
}
